import React, { useState } from 'react';
import { FiPlus, FiTrash2, FiCheck } from 'react-icons/fi';
import StatusDropdown from './StatusDropdown';
import AssigneePicker from './AssigneePicker';

const SubtasksManager = ({ subtasks = [], onChange, token, editMode }) => {
  const [newSubtask, setNewSubtask] = useState('');

  const handleAddSubtask = () => {
    if (!newSubtask.trim()) return;
    
    const subtask = {
      name: newSubtask,
      status: 'Not Started',
      assignee: null,
      startDate: null,
      dueDate: null,
      progress: 0,
      completed: false
    };
    
    onChange([...subtasks, subtask]);
    setNewSubtask('');
  };

  const handleUpdateSubtask = (index, field, value) => {
    const updated = [...subtasks];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const handleDeleteSubtask = (index) => {
    const updated = subtasks.filter((_, i) => i !== index);
    onChange(updated);
  };

  const handleToggleComplete = (index) => {
    const updated = [...subtasks];
    updated[index] = { 
      ...updated[index], 
      completed: !updated[index].completed,
      status: !updated[index].completed ? 'Done' : 'Not Started',
      progress: !updated[index].completed ? 100 : 0
    };
    onChange(updated);
  };

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    return dateStr.split('T')[0];
  };

  return (
    <div className="subtasks-manager">
      <div className="subtasks-header">
        <h4 style={{ fontSize: '14px', fontWeight: 600, margin: 0 }}>Subtasks ({subtasks.length})</h4>
      </div>
      
      {editMode && (
        <div className="subtask-add" style={{ marginBottom: '12px' }}>
          <input
            type="text"
            className="input"
            value={newSubtask}
            onChange={(e) => setNewSubtask(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddSubtask()}
            placeholder="Add subtask..."
            style={{ marginBottom: '8px' }}
            data-testid="add-subtask-input"
          />
          <button 
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={handleAddSubtask}
            data-testid="add-subtask-button"
          >
            <FiPlus size={14} />
            <span>Add Subtask</span>
          </button>
        </div>
      )}
      
      <div className="subtasks-list">
        {subtasks.map((subtask, index) => (
          <div key={index} className="subtask-item" data-testid="subtask-item">
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
              {editMode ? (
                <button
                  type="button"
                  onClick={() => handleToggleComplete(index)}
                  style={{
                    background: subtask.completed ? '#1DBF73' : 'transparent',
                    border: `2px solid ${subtask.completed ? '#1DBF73' : 'var(--border-medium)'}`,
                    borderRadius: '4px',
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginTop: '2px'
                  }}
                >
                  {subtask.completed && <FiCheck size={14} color="white" />}
                </button>
              ) : (
                <div
                  style={{
                    background: subtask.completed ? '#1DBF73' : 'transparent',
                    border: `2px solid ${subtask.completed ? '#1DBF73' : 'var(--border-medium)'}`,
                    borderRadius: '4px',
                    width: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginTop: '2px'
                  }}
                >
                  {subtask.completed && <FiCheck size={14} color="white" />}
                </div>
              )}
              
              <div style={{ flex: 1 }}>
                {editMode ? (
                  <input
                    type="text"
                    className="input"
                    value={subtask.name}
                    onChange={(e) => handleUpdateSubtask(index, 'name', e.target.value)}
                    style={{ marginBottom: '8px', fontSize: '14px' }}
                  />
                ) : (
                  <div style={{ 
                    fontSize: '14px', 
                    fontWeight: 500, 
                    textDecoration: subtask.completed ? 'line-through' : 'none',
                    color: subtask.completed ? 'var(--text-secondary)' : 'var(--text-primary)',
                    marginBottom: '8px'
                  }}>
                    {subtask.name}
                  </div>
                )}
                
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ minWidth: editMode ? '120px' : 'auto' }}>
                    {editMode ? (
                      <StatusDropdown
                        value={subtask.status}
                        onChange={(newStatus) => {
                          handleUpdateSubtask(index, 'status', newStatus);
                          handleUpdateSubtask(index, 'progress', newStatus === 'Done' ? 100 : subtask.progress);
                        }}
                      />
                    ) : (
                      <span className={`status-pill status-${subtask.status?.toLowerCase().replace(' ', '-')}`}
                        style={{ fontSize: '11px', padding: '2px 8px' }}
                      >
                        {subtask.status}
                      </span>
                    )}
                  </div>
                  
                  {editMode && (
                    <div style={{ minWidth: '100px' }}>
                      <AssigneePicker
                        assignees={subtask.assignee ? [subtask.assignee] : []}
                        onChange={(assignees) => handleUpdateSubtask(index, 'assignee', assignees[0] || null)}
                        token={token}
                      />
                    </div>
                  )}
                  
                  {(editMode || subtask.startDate) && (
                    <div>
                      {editMode ? (
                        <input
                          type="date"
                          className="input"
                          value={formatDateForInput(subtask.startDate)}
                          onChange={(e) => handleUpdateSubtask(index, 'startDate', e.target.value ? new Date(e.target.value).toISOString() : null)}
                          style={{ fontSize: '12px', padding: '4px 8px' }}
                        />
                      ) : (
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                          Start: {new Date(subtask.startDate).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  )}
                  
                  {(editMode || subtask.dueDate) && (
                    <div>
                      {editMode ? (
                        <input
                          type="date"
                          className="input"
                          value={formatDateForInput(subtask.dueDate)}
                          onChange={(e) => handleUpdateSubtask(index, 'dueDate', e.target.value ? new Date(e.target.value).toISOString() : null)}
                          style={{ fontSize: '12px', padding: '4px 8px' }}
                        />
                      ) : (
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                          Due: {new Date(subtask.dueDate).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  )}
                  
                  {editMode && (
                    <div>
                      <input
                        type="number"
                        className="input"
                        value={subtask.progress}
                        onChange={(e) => handleUpdateSubtask(index, 'progress', parseInt(e.target.value) || 0)}
                        min="0"
                        max="100"
                        style={{ width: '60px', fontSize: '12px', padding: '4px 8px' }}
                      />
                    </div>
                  )}
                </div>
              </div>
              
              {editMode && (
                <button
                  type="button"
                  onClick={() => handleDeleteSubtask(index)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <FiTrash2 size={14} />
                </button>
              )}
            </div>
          </div>
        ))}
        
        {subtasks.length === 0 && !editMode && (
          <div style={{ 
            textAlign: 'center', 
            padding: '16px', 
            color: 'var(--text-secondary)',
            fontSize: '13px'
          }}>
            No subtasks
          </div>
        )}
      </div>
    </div>
  );
};

export default SubtasksManager;